import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as amplitude from "@amplitude/analytics-browser";
import { PhoneInput, Button } from '@unified-checkout/design-system';
import type { IField } from '@app/dashboard/types/outerLayout/common';
import type { IRegisterPhoneNumberVerification } from '@app/dashboard/types/outerLayout/register';
import {
  isFormValid,
  phoneNumberValidation,
  required
} from '@app/dashboard/utils/formValidations';
import { addToLocalStorage } from '@app/dashboard/utils/manageLocalStorage';
import { iso3ToIso2 } from '@app/dashboard/utils/countriesUtils';
import { getTermsUrlFromDomain } from '@app/dashboard/utils/getTermsUrlFromDomain';
import { COUNTRIES_INFO } from '@app/dashboard/constants/countries';
import { leadsSubmitPhone, registerSubmitPhone } from '@app/dashboard/services/outer_layout/register';
import { useEnterKeyPress } from '@app/dashboard/hooks/useEnterKeyPress';
import { LOCAL_STORAGE_KEYS } from '@app/dashboard/constants/local-storage-keys';
import { country } from '../utils';
import styles from "../register.module.scss";

const FieldNames = {
  PHONE_NUMBER: "phone",
};

const fieldsInitialState = {
  [FieldNames.PHONE_NUMBER]: {
    value: '',
    isValid: undefined,
    errorMsg: '',
    validations: [required, phoneNumberValidation(country)]
  }
};

const SUBMIT_BTN_ID = 'phone-number-verification-submit-btn';

const PhoneNumberVerification = (
  {
    setPhoneNumber,
    validateField,
    loading,
    setLoading,
    setCurrentStep,
    STEPS,
    redirectToLogin,
    replacedServerErrMsg,
    leadToken
  }: IRegisterPhoneNumberVerification
) => {
  const { t } = useTranslation();
  useEnterKeyPress(SUBMIT_BTN_ID);

  const [fields, setFields] = useState<IField>(fieldsInitialState);
  const [serverErrMsg, setServerErrMsg] = useState('');

  const handlePhoneChange = (number: string) => {
    setServerErrMsg("");
    const numWithoutPhoneCountryCode = number.replace(COUNTRIES_INFO[country].phone_country_code, '');

    const newState = fields;
    newState[FieldNames.PHONE_NUMBER].value = numWithoutPhoneCountryCode;

    setFields(newState);
    validateField(fields, setFields, FieldNames.PHONE_NUMBER, numWithoutPhoneCountryCode);
  };

  const handlePhoneSubmit = () => {
    setServerErrMsg("");
    setLoading(true);

    const phoneNumber = fields[FieldNames.PHONE_NUMBER].value as string;
    const countryCode = COUNTRIES_INFO[country].phone_country_code;
    const alreadyRegisteredMsg = "This phone number is already registered.";

    registerSubmitPhone(phoneNumber, country, countryCode)
      .then(async (response) => {
        const message = response?.data?.message;

        amplitude.track('sign_up_phone_number');

        if (response.data.active || message === alreadyRegisteredMsg) {
          setServerErrMsg(t('outerLayout.form.register.errors.phoneExists'));
        } else {
          const token = response.data.token;
          await addToLocalStorage(LOCAL_STORAGE_KEYS.SIGNUP_BY_PHONE_TOKEN, token);
          setPhoneNumber(phoneNumber);

          setCurrentStep(STEPS.OTP);
        }
      })
      .catch((error) => {
        const status = error.response?.status;
        const data = error.response?.data;
        const message = error.response?.data?.message;

        if (status == 401 && message === alreadyRegisteredMsg) {
          setServerErrMsg(t('outerLayout.form.register.errors.phoneExists'));
        } else if (data) {
          setServerErrMsg(message || t('errors.somethingWentWrong'));
        } else {
          setServerErrMsg(t('errors.somethingWentWrong'));
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleLeadsPhoneSubmit = () => {
    setServerErrMsg("");
    setLoading(true);

    const phoneNumber = fields[FieldNames.PHONE_NUMBER].value as string;

    leadsSubmitPhone(phoneNumber, leadToken)
      .then(() => {
        setPhoneNumber(phoneNumber);
        setCurrentStep(STEPS.OTP);
      })
      .catch((error) => {
        const status = error.response?.status;
        const data = error.response?.data;
        const message = error.response?.data?.message;
        const phoneAlreadyRegisteredMsg = data?.phone_number?.message;
        const youDontHavePermissionMsg = "You do not have permission to perform this action.";

        if (phoneAlreadyRegisteredMsg) {
          setServerErrMsg(t('outerLayout.form.register.errors.phoneExists'));
        } else if (status === 403 && message === youDontHavePermissionMsg) {
          setServerErrMsg(t('outerLayout.form.errors.tryInAnHour'))
        } else if (message) {
          setServerErrMsg(message)
        } else {
          setServerErrMsg(t('errors.somethingWentWrong'));
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <PhoneInput
        id={FieldNames.PHONE_NUMBER}
        name={FieldNames.PHONE_NUMBER}
        label={t('outerLayout.form.labels.phoneNumber')}
        placeholder={t('outerLayout.form.register.placeholders.enterUrPhoneNum')}
        disableSelectCountry
        onChangeHandler={handlePhoneChange}
        country={iso3ToIso2(country)}
        error={fields[FieldNames.PHONE_NUMBER].errorMsg}
        disabled={loading}
      />
      <div className='form__submit'>
        {serverErrMsg &&
          <div className='form__submit__server-err'>{
            serverErrMsg.includes('{{loginLink}}') ?
              replacedServerErrMsg(serverErrMsg)
              :
              serverErrMsg
          }</div>
        }
        <Button
          id={SUBMIT_BTN_ID}
          size='md'
          color='primary'
          variant='contained'
          text={loading ? undefined : t('outerLayout.form.next')}
          loading={loading}
          disabled={!isFormValid(fields) || loading}
          onClickHandler={leadToken ? handleLeadsPhoneSubmit : handlePhoneSubmit}
        />
      </div>
      <div
        className={styles["register-form__terms-and-conditions"]}
        dangerouslySetInnerHTML={{ __html: t('outerLayout.form.termsAndConditions', { url: getTermsUrlFromDomain() }) }}
      ></div>
      <div className={styles["register-form__already-have-account"]}>
        <span className={styles["register-form__already-have-account__text"]}>{t('outerLayout.form.register.alreadyHaveAcc')}</span>
        <div className={styles["register-form__already-have-account__action"]}>
          <Button
            size='sm'
            color='primary'
            variant='link'
            text={t('outerLayout.form.register.signIn')}
            onClickHandler={redirectToLogin}
          />
        </div>
      </div>
    </>
  )
}

export default PhoneNumberVerification;